/**
 * Buffers projected `SessionEvent`s for one relay session and publishes them
 * into the React Query cache under the events query key, so existing consumers
 * read AHP-driven events through the same path as the REST source.
 *
 * Implements {@link ProjectorSink}; an {@link AhpActionProjector} drives it from
 * the connection runtime's snapshot + live action callbacks. All writes are
 * generation-guarded so callbacks arriving after disposal are dropped.
 */

import type {QueryClient} from '@github-ui/react-query'
import type {SessionEvent} from '../types/events'
import type {EventsQueryData} from '../utils/fetch-session-events'
import {generateEventsQueryKey} from '../utils/fetch-session-events'
import {uuid} from '../utils/uuid'
import {AhpActionProjector} from '@github-ui/ahp-relay/session-host/ahp-action-projector'
import type {
  ProjectorSink,
  ProjectedPermissionRequest,
  ProjectedPermissionResult,
  ProjectedToolRequest,
  ProjectedToolResult,
} from '@github-ui/ahp-relay/session-host/projector-sink'
import type {RelaySessionSink} from '@github-ui/ahp-relay/relay-registry'
import type {SessionState, ActionEnvelope} from '@microsoft/agent-host-protocol'

function makeEvent(type: string, data: unknown, ephemeral = false): SessionEvent {
  return {
    id: uuid(),
    timestamp: new Date().toISOString(),
    parentId: null,
    ...(ephemeral ? {ephemeral: true} : {}),
    type,
    data,
  } as unknown as SessionEvent
}

export class RelaySessionEvents implements ProjectorSink, RelaySessionSink {
  private readonly projector = new AhpActionProjector(this)
  private events: SessionEvent[] = []
  private disposed = false
  private readonly taskId: string
  private readonly queryClient: QueryClient

  constructor(taskId: string, queryClient: QueryClient) {
    this.taskId = taskId
    this.queryClient = queryClient
  }

  /** Take ownership of the events query key: cancel any in-flight fetch and seed an empty buffer. */
  start(): void {
    if (this.disposed) return
    void this.queryClient.cancelQueries({queryKey: generateEventsQueryKey(this.taskId)})
    this.writeCache()
  }

  /** Bootstrap from a subscription snapshot. */
  applySnapshot(state: SessionState): void {
    if (this.disposed) return
    this.projector.projectSnapshot(state)
  }

  /** Fold a single live action. */
  applyAction(env: ActionEnvelope): void {
    if (this.disposed) return
    this.projector.projectAction(env)
  }

  dispose(): void {
    this.disposed = true
  }

  private push(event: SessionEvent): void {
    if (this.disposed) return
    this.events = [...this.events, event]
    this.writeCache()
  }

  private writeCache(): void {
    this.queryClient.setQueryData<EventsQueryData>(generateEventsQueryKey(this.taskId), {
      events: this.events,
      afterCursor: null,
      total: this.events.length,
    })
  }

  // ─── ProjectorSink ──────────────────────────────────────────────────────────

  userMessage(_turnId: string, content: string): void {
    this.push(makeEvent('user.message', {content}))
  }

  turnStart(turnId: string): void {
    this.push(makeEvent('assistant.turn_start', {turnId}))
  }

  messageFinal(messageId: string, content: string): void {
    this.push(makeEvent('assistant.message', {messageId, content}))
  }

  permissionRequested(request: ProjectedPermissionRequest): void {
    this.push(
      makeEvent('permission.requested', {
        requestId: request.requestId,
        permissionRequest: {
          kind: 'custom-tool',
          toolCallId: request.toolCallId,
          toolName: request.toolName,
          toolDescription: request.detail || request.toolName,
          args: {},
        },
      }),
    )
  }

  permissionCompleted(result: ProjectedPermissionResult): void {
    this.push(
      makeEvent('permission.completed', {
        requestId: result.requestId,
        toolCallId: result.toolCallId,
        result: {kind: result.approved ? 'approved' : 'denied-interactively-by-user'},
      }),
    )
  }

  toolRequested(request: ProjectedToolRequest): void {
    this.push(
      makeEvent('assistant.message', {
        messageId: uuid(),
        content: '',
        toolRequests: [
          {
            toolCallId: request.toolCallId,
            name: request.toolName,
            arguments: request.args,
            ...(request.toolTitle ? {toolTitle: request.toolTitle} : {}),
          },
        ],
      }),
    )
  }

  toolCompleted(result: ProjectedToolResult): void {
    this.push(
      makeEvent('tool.execution_complete', {
        toolCallId: result.toolCallId,
        success: result.success,
        ...(result.success
          ? {
              result: {
                content: result.content,
                ...(result.detailedContent ? {detailedContent: result.detailedContent} : {}),
              },
            }
          : {error: {message: result.errorMessage || 'Tool call failed'}}),
      }),
    )
  }

  turnEnd(turnId: string): void {
    this.push(makeEvent('assistant.turn_end', {turnId}))
  }

  idle(_aborted: boolean): void {
    this.push(makeEvent('session.idle', {}, true))
  }

  turnError(_turnId: string, message: string): void {
    this.push(makeEvent('assistant.message', {messageId: uuid(), content: `⚠️ Turn error: ${message}`}))
  }
}
